import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CatalogProductStatus } from '@stamp/merch';
import { StatusBadge } from '../components/StatusBadge';
import { fetchProducts, type CatalogProduct, type Brand } from '../api';

const BRANDS: ('all' | Brand)[] = ['all', 'Calitoy', 'Kurced', 'CalitoyMuse', 'Custom'];
const STATUSES: ('all' | CatalogProductStatus)[] = ['all', 'active', 'draft', 'retired'];

const STATUS_TO_JOB: Record<CatalogProductStatus, 'active' | 'draft' | 'retired'> = {
  active: 'active',
  draft: 'draft',
  retired: 'retired',
};

export function Products() {
  const navigate = useNavigate();
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [brandFilter, setBrandFilter] = useState<'all' | Brand>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | CatalogProductStatus>('all');
  const [collectionFilter, setCollectionFilter] = useState('all');

  useEffect(() => {
    fetchProducts().then((p) => {
      setProducts(p);
      setLoading(false);
    });
  }, []);

  const collections = useMemo(() => {
    return [...new Set(products.map((p) => p.collection))].sort();
  }, [products]);

  const filtered = useMemo(() => {
    return products.filter((p) => {
      if (brandFilter !== 'all' && p.brand !== brandFilter) return false;
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      if (collectionFilter !== 'all' && p.collection !== collectionFilter) return false;
      return true;
    });
  }, [products, brandFilter, statusFilter, collectionFilter]);

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ margin: 0 }}>Products</h1>
        <button className="btn btn--primary" onClick={() => navigate('/new')}>
          + New Design
        </button>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <p className="stat-card__label">Total Products</p>
          <p className="stat-card__value">{products.length}</p>
        </div>
        <div className="stat-card">
          <p className="stat-card__label">Active</p>
          <p className="stat-card__value">{products.filter((p) => p.status === 'active').length}</p>
        </div>
        <div className="stat-card">
          <p className="stat-card__label">Drafts</p>
          <p className="stat-card__value">{products.filter((p) => p.status === 'draft').length}</p>
        </div>
        <div className="stat-card">
          <p className="stat-card__label">Avg Price</p>
          <p className="stat-card__value">
            ${products.length > 0 ? (products.reduce((s, p) => s + p.price, 0) / products.length).toFixed(0) : '0'}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="filter-bar">
        <span className="filter-bar__label">Brand:</span>
        {BRANDS.map((b) => (
          <button
            key={b}
            className={`filter-chip ${brandFilter === b ? 'filter-chip--active' : ''}`}
            onClick={() => setBrandFilter(b)}
          >
            {b === 'all' ? 'All' : b}
          </button>
        ))}
      </div>
      <div className="filter-bar">
        <span className="filter-bar__label">Status:</span>
        {STATUSES.map((s) => (
          <button
            key={s}
            className={`filter-chip ${statusFilter === s ? 'filter-chip--active' : ''}`}
            onClick={() => setStatusFilter(s)}
          >
            {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>
      {collections.length > 0 && (
        <div className="filter-bar">
          <span className="filter-bar__label">Collection:</span>
          <button
            className={`filter-chip ${collectionFilter === 'all' ? 'filter-chip--active' : ''}`}
            onClick={() => setCollectionFilter('all')}
          >
            All
          </button>
          {collections.map((c) => (
            <button
              key={c}
              className={`filter-chip ${collectionFilter === c ? 'filter-chip--active' : ''}`}
              onClick={() => setCollectionFilter(c)}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      {/* Product Grid */}
      {filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__icon">&#128716;</div>
          <div className="empty-state__title">No products found</div>
          <p>Try adjusting your filters.</p>
        </div>
      ) : (
        <div className="product-grid">
          {filtered.map((p) => (
            <div
              key={p.id}
              className="product-card"
              onClick={() => p.jobId ? navigate(`/job/${p.jobId}`) : undefined}
            >
              <img className="product-card__image" src={p.image} alt={p.name} />
              <div className="product-card__body">
                <div className="product-card__name">{p.name}</div>
                <div className="product-card__brand">{p.brand} - {p.collection}</div>
                <div className="product-card__footer">
                  <span className="product-card__price">${p.price.toFixed(2)}</span>
                  {p.variants.length > 0 ? (
                    <span className="product-card__variants">{p.variants.length} variants</span>
                  ) : (
                    <span style={{ fontSize: '0.72rem', color: 'var(--ink-faint)' }}>No variants</span>
                  )}
                </div>
                <div style={{ marginTop: '8px' }}>
                  <StatusBadge status={STATUS_TO_JOB[p.status] === 'active' ? 'published' : STATUS_TO_JOB[p.status] === 'draft' ? 'brief_draft' : 'archived'} size="sm" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
